import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";

const Counter = ({ to, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const duration = 1600;
    const start = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(to * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);

  // Render year-like values (>= 1900) without thousand separators
  const display = to >= 1900 ? String(val) : val.toLocaleString();

  return (
    <span ref={ref} className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-white">
      {display}
      <span className="accent-gradient-text">{suffix}</span>
    </span>
  );
};

export default function Impact() {
  const { t } = useLang();
  return (
    <section id="impact" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="rounded-3xl border border-white/[0.06] bg-gradient-to-br from-[#0B101E] via-[#070B15] to-[#050810] p-8 md:p-14 relative overflow-hidden">
          <div className="halo w-[520px] h-[520px] bg-[#00F0FF]/10 -top-40 -right-32" />
          <div className="halo w-[420px] h-[420px] bg-[#007AFF]/15 -bottom-40 -left-24" />

          <div className="relative">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#00F0FF]">
                {t.impact.eyebrow}
              </span>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tighter text-white">
                {t.impact.title}
              </h2>
              <p className="mt-4 text-base text-[#8B95A5] leading-relaxed">{t.impact.sub}</p>
            </div>

            <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
              {t.impact.stats.map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  data-testid={`impact-stat-${i}`}
                  className="flex flex-col"
                >
                  <Counter to={s.value} suffix={s.suffix} />
                  <span className="mt-3 text-xs sm:text-sm uppercase tracking-[0.18em] text-[#8B95A5]">
                    {s.label}
                  </span>
                </motion.div>
              ))}
            </div>

            <div className="mt-12 flex items-start gap-3 text-sm text-[#8B95A5] max-w-3xl">
              <span className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[#00F0FF] flex-shrink-0" />
              <p>{t.impact.note}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
